import React, { createContext, useContext, useState, ReactNode, useEffect, useMemo } from 'react';
import { User } from '../types';
import { apiPost, ApiError } from '../utils/api';
import { AuthContextType, AuthUser } from '../types';
import { apiRequest } from '../utils/api';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [token, setToken] = useState<string | null>(null);

  // Utility function to extract roles from Azure AD token
  const extractRolesFromToken = (authToken: string): string[] => {
    try {
      // Decode JWT token payload
      const payload = JSON.parse(atob(authToken.split('.')[1]));
      console.log('🔍 Token payload keys:', Object.keys(payload));
      
      // Extract roles from Azure AD token
      const roles = payload.roles || [];
      console.log('🎭 Found roles in token:', roles);
      
      // Convert to uppercase to match frontend expectations
      return roles.map((role: string) => role.toUpperCase());
    } catch (error) {
      console.warn('⚠️ Failed to extract roles from token:', error);
      return [];
    }
  };

  useEffect(() => {
    // Check for stored token on app load
    const storedToken = localStorage.getItem('auth_token');
    if (storedToken) {
      setToken(storedToken);
      // Validate token by making a test request
      validateToken(storedToken);
    }
  }, []);

  const validateToken = async (authToken: string) => {
    try {
      console.log('🔍 Validating stored token...');
      
      // Extract roles from the token itself
      const tokenRoles = extractRolesFromToken(authToken);
      console.log('🎭 Extracted roles from token:', tokenRoles);
      
      // Use the /users/me endpoint to validate the token and get user info
      const response = await apiRequest('/users/me', {
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });
      
      if (response.ok) {
        // Token is valid, get user info from the API response
        const userProfile = await response.json();
        setUser({
          user_id: userProfile.user_id,
          email: userProfile.email,
          display_name: userProfile.display_name,
          roles: tokenRoles, // Use roles extracted from the token
          current_teams: userProfile.current_teams || [] // Include team data
        });
        setIsAuthenticated(true);
        console.log('✅ Token validation successful for user:', userProfile.email, 'with roles:', tokenRoles);
      } else {
        // Token is invalid
        console.warn('❌ Token validation failed - status:', response.status, 'clearing auth state');
        logout();
      }
    } catch (error) {
      console.error('❌ Token validation failed with error:', error, 'clearing auth state');
      logout();
    }
  };

  const login = (email: string, authToken: string) => {
    console.log('🔐 AuthContext.login called with:', { email, tokenLength: authToken.length });
    
    try {
      // Extract roles from Azure AD token
      const azureAdRoles = extractRolesFromToken(authToken);
      
      // Decode JWT to get user info
      const payload = JSON.parse(atob(authToken.split('.')[1]));
      console.log('📋 Decoded token payload keys:', Object.keys(payload));

      // Sub is not always a UUID, but people may have already joined teams using it.
      // To maintain backwards compatibility we need to use the sub where necessary and where not fall back to the oid.
      const sub_is_uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(payload.sub);
      const authUser: AuthUser = {
        user_id: sub_is_uuid ? payload.sub : payload.oid,
        email: payload.email || email,
        display_name: payload.name || email,
        roles: azureAdRoles // Use extracted Azure AD roles
      };
      
      console.log('👤 Created authUser:', authUser, payload.sub, payload.oid);
      
      setUser(authUser);
      setToken(authToken);
      setIsAuthenticated(true);
      localStorage.setItem('auth_token', authToken);
      
      console.log('✅ Login successful - user authenticated with roles:', azureAdRoles);
    } catch (error) {
      console.error('❌ Failed to decode token:', error);
      // Still try to authenticate even if token decoding fails
      setUser({
        user_id: email,
        email: email,
        display_name: email,
        roles: []
      });
      setToken(authToken);
      setIsAuthenticated(true);
      localStorage.setItem('auth_token', authToken);
      console.log('✅ Login successful with fallback user info');
    }
  };

  const logout = () => {
    console.log('🚪 Logging out user and clearing auth state');
    setUser(null);
    setToken(null);
    setIsAuthenticated(false);
    localStorage.removeItem('auth_token');
  };

  const refreshUser = async () => {
    if (!token) {
      console.warn('⚠️ Cannot refresh user - no token available');
      return;
    }

    try {
      console.log('🔄 Refreshing user data...');
      const response = await apiRequest('/users/me', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const userProfile = await response.json();
        const currentRoles = user?.roles || extractRolesFromToken(token);
        
        setUser({
          user_id: userProfile.user_id,
          email: userProfile.email,
          display_name: userProfile.display_name,
          roles: currentRoles,
          current_teams: userProfile.current_teams || []
        });
        
        console.log('✅ User data refreshed successfully:', userProfile.email);
      } else {
        console.warn('⚠️ Failed to refresh user data - status:', response.status);
      }
    } catch (error) {
      console.error('❌ Error refreshing user data:', error);
    }
  };

  const value: AuthContextType = useMemo(() => ({
    isAuthenticated,
    user,
    token,
    login,
    logout,
    refreshUser
  }), [isAuthenticated, user, token]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
